import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../web/components-v2/Button';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../../../web/components-v2/Collapsible';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../../web/components-v2/Dialog';
import { TextArea } from '../../../../../web/components-v2/TextArea';
import { TextInput } from '../../../../../web/components/TextInput';
import { styles } from './styles';

interface AIAgentTemplateVariablesProps {
	variables: Record<string, string>;
	onChange: (variables: Record<string, string>) => void;
	objectivePrompt?: string;
	disabled?: boolean;
}

interface EditVariableModalProps {
	isOpen: boolean;
	originalKey: string;
	variableKey: string;
	value: string;
	existingKeys: string[];
	isNew?: boolean;
	onSave: (originalKey: string, newKey: string, value: string, isNew?: boolean) => void;
	onClose: () => void;
}

function EditVariableModal({
	isOpen,
	originalKey,
	variableKey,
	value,
	existingKeys,
	isNew,
	onSave,
	onClose,
}: EditVariableModalProps) {
	const [editKey, setEditKey] = React.useState(variableKey);
	const [editValue, setEditValue] = React.useState(value);
	const [keyError, setKeyError] = React.useState<string | null>(null);

	React.useEffect(() => {
		setEditKey(variableKey);
		setEditValue(value);
		setKeyError(null);
	}, [variableKey, value, isOpen]);

	const handleSave = () => {
		const trimmedKey = editKey.trim();
		if (!trimmedKey) {
			setKeyError('Variable name is required');
			return;
		}
		if ((isNew || trimmedKey !== originalKey) && existingKeys.includes(trimmedKey)) {
			setKeyError('A variable with this name already exists');
			return;
		}
		onSave(originalKey, trimmedKey, editValue, isNew);
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
			<DialogPopup styles={[styles.editModal]}>
				<DialogHeader>
					<DialogTitle>{isNew ? 'Add Variable' : 'Edit Variable'}</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className={css(styles.modalField)}>
						<label className={css(styles.modalLabel)}>Variable Name</label>
						<TextInput
							type='text'
							value={editKey}
							onChange={e => {
								setEditKey(e.target.value);
								setKeyError(null);
							}}
							placeholder='e.g., companyName'
						/>
						{keyError && <div className={css(styles.modalError)}>{keyError}</div>}
					</div>
					<div className={css(styles.modalField)}>
						<label className={css(styles.modalLabel)}>Value</label>
						<TextArea
							styles={[styles.modalTextArea]}
							value={editValue}
							onChange={e => setEditValue(e.target.value)}
							placeholder='Enter the value for this variable'
						/>
					</div>
				</DialogBody>
				<DialogFooter>
					<Button type='button' variant='secondary' onClick={onClose}>
						Cancel
					</Button>
					<Button type='button' variant='primary' onClick={handleSave}>
						Save
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}

interface PromptPreviewModalProps {
	isOpen: boolean;
	objectivePrompt: string;
	variables: Record<string, string>;
	onClose: () => void;
}

function PromptPreviewModal({ isOpen, objectivePrompt, variables, onClose }: PromptPreviewModalProps) {
	const renderPreviewContent = () => {
		if (!objectivePrompt) return null;

		const parts: React.ReactNode[] = [];
		let lastIndex = 0;
		const regex = /\{\{(\w+)\}\}/g;
		let match;

		while ((match = regex.exec(objectivePrompt)) !== null) {
			if (match.index > lastIndex) {
				parts.push(objectivePrompt.slice(lastIndex, match.index));
			}

			const variableName = match[1];
			const variableValue = variables[variableName];

			if (variableValue !== undefined && variableValue !== '') {
				parts.push(
					<span
						key={`${match.index}-${variableName}`}
						className={css(styles.highlightedVariable)}
						title={`{{${variableName}}}`}
					>
						{variableValue}
					</span>
				);
			} else {
				parts.push(
					<span
						key={`${match.index}-${variableName}`}
						className={css(styles.undefinedVariable)}
						title='Variable not defined'
					>
						{`{{${variableName}}}`}
					</span>
				);
			}

			lastIndex = match.index + match[0].length;
		}

		if (lastIndex < objectivePrompt.length) {
			parts.push(objectivePrompt.slice(lastIndex));
		}

		return parts;
	};

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
			<DialogPopup styles={[styles.previewModal]}>
				<DialogHeader>
					<DialogTitle>Prompt Preview</DialogTitle>
				</DialogHeader>
				<DialogBody styles={[styles.previewModalContent]}>
					<div className={css(styles.previewPromptContent)}>{renderPreviewContent()}</div>
				</DialogBody>
				<DialogFooter>
					<Button type='button' variant='secondary' onClick={onClose}>
						Close
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}

function extractVariablesFromPrompt(prompt: string): string[] {
	if (!prompt) return [];
	const regex = /\{\{(\w+)\}\}/g;
	const foundVariables = new Set<string>();
	let match;

	while ((match = regex.exec(prompt)) !== null) {
		foundVariables.add(match[1]);
	}

	return Array.from(foundVariables);
}

export function AIAgentTemplateVariables({
	variables,
	onChange,
	objectivePrompt,
	disabled,
}: AIAgentTemplateVariablesProps) {
	const [isExpanded, setIsExpanded] = React.useState(false);
	const [editingVariable, setEditingVariable] = React.useState<{ key: string; value: string; isNew?: boolean } | null>(
		null
	);
	const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);

	const variableEntries = Object.entries(variables);
	const promptVariables = extractVariablesFromPrompt(objectivePrompt || '');
	const undefinedPromptVariables = promptVariables.filter(v => variables[v] === undefined);
	const variableCount = variableEntries.length;

	const handleSaveVariable = (originalKey: string, newKey: string, value: string, isNew?: boolean) => {
		const newVariables = { ...variables };
		if (!isNew && originalKey !== newKey) {
			delete newVariables[originalKey];
		}
		newVariables[newKey] = value;
		onChange(newVariables);
	};

	const handleDeleteVariable = (key: string) => {
		const newVariables = { ...variables };
		delete newVariables[key];
		onChange(newVariables);
	};

	const handleAddVariable = () => {
		setIsExpanded(true);
		setEditingVariable({ key: '', value: '', isNew: true });
	};

	const handleAddUndefinedVariable = (variableName: string) => {
		onChange({ ...variables, [variableName]: '' });
		setIsExpanded(true);
		setEditingVariable({ key: variableName, value: '' });
	};

	const truncateValue = (value: string, maxLength = 50) => {
		if (!value) return <span className={css(styles.emptyValue)}>No value set</span>;
		if (value.length <= maxLength) return value;
		return value.substring(0, maxLength) + '...';
	};

	return (
		<div className={css(styles.container)}>
			<Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
				<CollapsibleTrigger className={css(styles.collapsibleHeader)}>
					<span className={css(styles.collapsibleIcon, isExpanded && styles.collapsibleIconOpen)}>&#9654;</span>
					<span className={css(styles.collapsibleTitle)}>Variables</span>
					{variableCount > 0 && <span className={css(styles.collapsibleCount)}>{variableCount}</span>}
				</CollapsibleTrigger>
				<CollapsiblePanel>
					<div className={css(styles.collapsibleContent)}>
						{variableEntries.length === 0 ? (
							<div className={css(styles.emptyState)}>
								No variables defined. Variables use the format {'{{variableName}}'} in prompts.
							</div>
						) : (
							<div className={css(styles.variablesList)}>
								{variableEntries.map(([key, value]) => (
									<div key={key} className={css(styles.variableItem)}>
										<div className={css(styles.variableInfo)}>
											<span className={css(styles.variableKey)}>{`{{${key}}}`}</span>
											<span className={css(styles.variableValue)}>{truncateValue(value)}</span>
										</div>
										<div className={css(styles.variableActions)}>
											<Button
												type='button'
												variant='secondary'
												onClick={() => setEditingVariable({ key, value })}
												disabled={disabled}
											>
												Edit
											</Button>
											<Button
												type='button'
												variant='secondary'
												onClick={() => handleDeleteVariable(key)}
												disabled={disabled}
											>
												Remove
											</Button>
										</div>
									</div>
								))}
							</div>
						)}

						{undefinedPromptVariables.length > 0 && (
							<div className={css(styles.undefinedVariables)}>
								<div className={css(styles.undefinedVariablesLabel)}>Variables found in prompt but not defined:</div>
								{undefinedPromptVariables.map(v => (
									<button
										key={v}
										type='button'
										className={css(styles.undefinedVariableChip)}
										onClick={() => handleAddUndefinedVariable(v)}
										disabled={disabled}
									>
										{`{{${v}}}`} <span>+</span>
									</button>
								))}
							</div>
						)}

						<div className={css(styles.actionsRow)}>
							<Button type='button' variant='secondary' onClick={handleAddVariable} disabled={disabled}>
								Add Variable
							</Button>
							{objectivePrompt && (variableCount > 0 || promptVariables.length > 0) && (
								<Button type='button' variant='secondary' onClick={() => setIsPreviewOpen(true)}>
									Preview Prompt
								</Button>
							)}
						</div>
					</div>
				</CollapsiblePanel>
			</Collapsible>

			{editingVariable && (
				<EditVariableModal
					isOpen={!!editingVariable}
					originalKey={editingVariable.key}
					variableKey={editingVariable.key}
					value={editingVariable.value}
					existingKeys={Object.keys(variables)}
					isNew={editingVariable.isNew}
					onSave={handleSaveVariable}
					onClose={() => setEditingVariable(null)}
				/>
			)}

			<PromptPreviewModal
				isOpen={isPreviewOpen}
				objectivePrompt={objectivePrompt || ''}
				variables={variables}
				onClose={() => setIsPreviewOpen(false)}
			/>
		</div>
	);
}
